import { store } from 'store';

import { handleAction } from './actionHandler';
import { UNMUTE_WS_URL } from './consts';
import { sophieDebugLog, sophieDebugUpdateStt } from './sophieDebug';
import {
  buildWsUrl,
  handleAssistantWsEvent,
  logSessionInit,
  logWsClosed,
  logWsConnected,
  logWsConnecting,
  logWsError,
  sendSessionUpdate,
} from './wsHandler';

// ── Types ────────────────────────────────────────────────────────────────────

interface OpusRecorder {
  encodedSamplePosition: number;
  ondataavailable: ((data: Uint8Array) => void) | null;
  start: (stream: MediaStream) => Promise<void>;
  stop: () => Promise<void>;
}

// ── Binary helpers ───────────────────────────────────────────────────────────

function uint8ToBase64(arr: Uint8Array): string {
  const CHUNK = 0x8000;
  let result = '';
  for (let i = 0; i < arr.length; i += CHUNK) {
    result += String.fromCharCode.apply(null, Array.from(arr.subarray(i, i + CHUNK)));
  }
  return btoa(result);
}

function base64ToUint8(b64: string): Uint8Array {
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

// ── Constants ────────────────────────────────────────────────────────────────

const VOICE_ASSETS_PATH = '/voice/';
const EQ_LEVELS_COUNT = 7;
const EQ_UPDATE_INTERVAL = 50; // ~20fps for store updates

// ── Singleton state (plain refs, NOT in MobX) ────────────────────────────────

let ws: WebSocket | null = null;
let audioCtx: AudioContext | null = null;
let recorder: OpusRecorder | null = null;
let decoder: Worker | null = null;
let outputWorklet: AudioWorkletNode | null = null;
let localStream: MediaStream | null = null;
let micAnalyser: AnalyserNode | null = null;
let serverAnalyser: AnalyserNode | null = null;
let micDuration = 0;

let connected = false;
let connecting = false;
let cleaningUp = false;

let eqAnimId: number | null = null;

// Mic mute: after user mutes, keep sending audio for a brief window
// so the backend VAD can detect silence and fire speech_stopped.
let hardMuted = false;
let muteSendTimeout: ReturnType<typeof setTimeout> | null = null;

// ── Public getters ───────────────────────────────────────────────────────────

export function getMicAnalyser(): AnalyserNode | null {
  return micAnalyser;
}

export function getServerAnalyser(): AnalyserNode | null {
  return serverAnalyser;
}

export function isConnected(): boolean {
  return connected;
}

export function isConnecting(): boolean {
  return connecting;
}

// ── EQ level pump (updates assistantStore.voiceLevels for Player) ─────────

function startLevelPump() {
  if (eqAnimId !== null) return;

  const pump = () => {
    eqAnimId = requestAnimationFrame(pump);

    if (!micAnalyser) {
      store.assistantStore.setVoiceLevels(null);
      return;
    }

    const bufLen = micAnalyser.frequencyBinCount;
    const data = new Uint8Array(bufLen);
    micAnalyser.getByteFrequencyData(data);

    const levels: number[] = [];
    for (let i = 0; i < EQ_LEVELS_COUNT; i++) {
      const idx = Math.floor((i * bufLen * 0.4) / EQ_LEVELS_COUNT);
      levels.push(data[idx] / 255);
    }
    store.assistantStore.setVoiceLevels(levels);
  };

  pump();
}

function stopLevelPump() {
  if (eqAnimId !== null) {
    cancelAnimationFrame(eqAnimId);
    eqAnimId = null;
  }
  store.assistantStore.setVoiceLevels(null);
}

// ── WS message handler ───────────────────────────────────────────────────────

function handleWsMessage(msg: MessageEvent) {
  let event: Record<string, unknown>;
  try {
    event = JSON.parse(msg.data as string);
  } catch {
    console.log('[Voice] Event parse error');
    return;
  }

  const { assistantStore } = store;

  handleAssistantWsEvent(event, {
    onAudioDelta: (delta) => {
      if (decoder) {
        const audioBytes = base64ToUint8(delta);
        decoder.postMessage({ command: 'decode', pages: audioBytes }, [audioBytes.buffer]);
      }
    },

    onInterrupted: () => {
      const text = assistantStore.finalizeAssistantMsg();
      if (text) assistantStore.saveMessage('assistant', text);
    },

    onSpeechStopped: () => {
      const userText = assistantStore.finalizeUserMsg();
      if (userText) {
        assistantStore.saveMessage('user', userText);
        sophieDebugUpdateStt('', userText);
        assistantStore.touchLastInteraction();
      }
    },

    onModeSwitch: (mode) => {
      store.commonStore.setMode(mode);
    },

    onAction: handleAction,

    onSttDelta: (delta) => {
      const id = assistantStore.ensureUserMsg();
      const msg = assistantStore.chatMessages.find((m) => m.id === id);
      if (
        msg?.text &&
        !msg.text.endsWith(' ') &&
        !delta.startsWith(' ') &&
        !delta.startsWith('.') &&
        !delta.startsWith(',') &&
        !delta.startsWith('!') &&
        !delta.startsWith('?')
      ) {
        assistantStore.appendToChatMessage(id, ' ');
      }
      assistantStore.appendToChatMessage(id, delta);
      sophieDebugUpdateStt(msg?.text ? msg.text + delta : delta);
    },
  });
}

// ── Start voice ──────────────────────────────────────────────────────────────

export async function startVoice(): Promise<void> {
  if (connected || connecting || cleaningUp) return;

  const { assistantStore } = store;
  connecting = true;

  try {
    // Request mic
    if (!localStream) {
      localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });
    }
    assistantStore.setVoiceState('connecting');

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
    audioCtx = new AudioCtxClass();
    if (audioCtx.state === 'suspended') await audioCtx.resume();

    // Mic analyser
    const micSource = audioCtx.createMediaStreamSource(localStream);
    micAnalyser = audioCtx.createAnalyser();
    micAnalyser.fftSize = 256;
    micAnalyser.smoothingTimeConstant = 0.7;
    micSource.connect(micAnalyser);

    // Output worklet + server analyser
    await audioCtx.audioWorklet.addModule(VOICE_ASSETS_PATH + 'audio-output-processor.js');
    outputWorklet = new AudioWorkletNode(audioCtx, 'audio-output-processor');
    serverAnalyser = audioCtx.createAnalyser();
    serverAnalyser.smoothingTimeConstant = 0.85;
    serverAnalyser.fftSize = 256;
    outputWorklet.connect(serverAnalyser);
    serverAnalyser.connect(audioCtx.destination);

    // Decoder worker
    decoder = new Worker(VOICE_ASSETS_PATH + 'decoderWorker.min.js');
    decoder.onmessage = (workerEvent) => {
      if (!workerEvent.data) return;
      const frame = workerEvent.data[0];
      if (outputWorklet) {
        outputWorklet.port.postMessage({ frame, type: 'audio', micDuration });
      }
    };
    decoder.postMessage({
      command: 'init',
      bufferLength: Math.round((960 * audioCtx.sampleRate) / 24000),
      decoderSampleRate: 24000,
      outputBufferSampleRate: audioCtx.sampleRate,
      resampleQuality: 0,
    });

    // Opus encoder
    const RecorderClass = window.Recorder;
    if (!RecorderClass) {
      throw new Error('Opus Recorder not loaded');
    }
    recorder = new RecorderClass({
      encoderPath: VOICE_ASSETS_PATH + 'encoderWorker.min.js',
      mediaTrackConstraints: {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
        video: false,
      },
      bufferLength: Math.round((960 * audioCtx.sampleRate) / 24000),
      encoderFrameSize: 20,
      encoderSampleRate: 24000,
      maxFramesPerPage: 2,
      numberOfChannels: 1,
      recordingGain: 1,
      resampleQuality: 3,
      encoderComplexity: 0,
      encoderApplication: 2049,
      streamPages: true,
    }) as OpusRecorder;

    recorder.ondataavailable = (data: Uint8Array) => {
      if (!recorder) return;
      micDuration = recorder.encodedSamplePosition / 48000;
      if (ws && ws.readyState === WebSocket.OPEN && !hardMuted) {
        ws.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: uint8ToBase64(data) }));
      }
    };

    // WebSocket
    const userId = assistantStore.ensureUserId();
    logWsConnecting(UNMUTE_WS_URL);
    ws = new WebSocket(buildWsUrl(UNMUTE_WS_URL, userId), ['realtime']);

    ws.onopen = () => {
      logWsConnected();
      const sophie = assistantStore.buildInstructions();
      logSessionInit(sophie);
      sendSessionUpdate(ws!, userId, sophie.instructions);
      assistantStore.touchLastInteraction();

      recorder!
        .start(localStream!)
        .then(() => console.log('[Voice] Opus recording started'))
        .catch((e: Error) => console.error('[Voice] Opus recorder start error:', e));

      connected = true;
      connecting = false;
      assistantStore.setVoiceState('active');
      assistantStore.setSophiePhase('listening');
      startLevelPump();
    };

    ws.onmessage = handleWsMessage;
    ws.onerror = (err) => logWsError((err as ErrorEvent).message);
    ws.onclose = (ev) => {
      logWsClosed(ev.code, ev.reason);
      if (connected) stopVoice();
    };
  } catch (err) {
    sophieDebugLog('[Voice] Error: ' + (err instanceof Error ? err.message : String(err)));
    assistantStore.setVoiceState('error');
    await stopVoice();
    setTimeout(() => assistantStore.setVoiceState('idle'), 2000);
  }
}

// ── Stop voice ───────────────────────────────────────────────────────────────

export async function stopVoice(): Promise<void> {
  if (cleaningUp) return;
  cleaningUp = true;
  connected = false;
  connecting = false;

  const { assistantStore } = store;

  try {
    const userText = assistantStore.finalizeUserMsg();
    if (userText) assistantStore.saveMessage('user', userText);
    const assistantText = assistantStore.finalizeAssistantMsg();
    if (assistantText) assistantStore.saveMessage('assistant', assistantText);
    assistantStore.touchLastInteraction();

    stopLevelPump();

    if (ws) {
      try {
        ws.close();
      } catch {
        /* noop */
      }
      ws = null;
    }
    if (recorder) {
      try {
        recorder.stop();
      } catch {
        /* noop */
      }
      recorder = null;
    }
    if (decoder) {
      try {
        decoder.terminate();
      } catch {
        /* noop */
      }
      decoder = null;
    }
    if (outputWorklet) {
      try {
        outputWorklet.disconnect();
      } catch {
        /* noop */
      }
      outputWorklet = null;
    }

    micAnalyser = null;
    serverAnalyser = null;
    micDuration = 0;

    if (localStream) {
      localStream.getTracks().forEach((t) => {
        try {
          t.stop();
        } catch {
          /* noop */
        }
      });
      localStream = null;
    }
    if (audioCtx) {
      try {
        await audioCtx.close();
      } catch {
        /* noop */
      }
      audioCtx = null;
    }
  } finally {
    cleaningUp = false;
    hardMuted = false;
    if (muteSendTimeout) {
      clearTimeout(muteSendTimeout);
      muteSendTimeout = null;
    }
    assistantStore.setVoiceState('idle');
    assistantStore.setIsMicMuted(false);
  }
}

// ── Mic mute/unmute ──────────────────────────────────────────────────────────

export function setMicMuted(muted: boolean): void {
  if (muteSendTimeout) {
    clearTimeout(muteSendTimeout);
    muteSendTimeout = null;
  }

  if (muted) {
    // Keep sending audio for 1s so backend VAD can detect end-of-speech,
    // then fully stop transmitting.
    hardMuted = false;
    muteSendTimeout = setTimeout(() => {
      hardMuted = true;
      muteSendTimeout = null;
    }, 1000);
  } else {
    hardMuted = false;
  }

  store.assistantStore.setIsMicMuted(muted);
}
